import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/core/lib/apiClient';
import { useSession } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import type { Post } from '@/core/models/post';
import type { Comment } from '@/core/models/comment';
import { useCommentStore } from '@/core/stores/commentStore';
import { useSessionStore } from '@/core/stores/sessionStore';
import { commentsService } from '@/core/services/commentsService';
import { discussionHref } from '@/core/services/discussionsService';
import { Button, Dialog, MetaRow, Textarea } from '@/ui';
import { DiscussionToolsContext, type Tool } from './context';
import { discussionTools, toolsUrl, type ToolsState, type SourceComment } from './service';
import { Card, Field, Quote, Row, Small, Stack } from './styles';

const titles: Record<Tool, string> = {
  search: 'Search this discussion',
  sources: 'Sources in this discussion',
  snooze: 'Pause discussion notifications',
  bookmark: 'Private bookmark note',
  labels: 'Private discussion labels',
  update: 'Add an author’s update',
  invite: 'Invite someone to this discussion',
  quote: 'Quote a passage',
  reply: 'Reply to selected comments',
  helpful: 'Choose a helpful answer',
  reconsider: 'What have you reconsidered?',
};

export function DiscussionToolsProvider({ post, children }: { post: Post; children: ReactNode }) {
  const { currentUser } = useSession();
  // 1.27.70: on the Videos pages (watch page), members without two-factor may still search and
  // read sources, but every tool that writes is locked. No-op on every other page.
  const gate = useWriteGate();
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useRef(0);
  useEffect(() => {
    navigation.current++;
  }, [location.key]);
  const [state, setState] = useState<ToolsState | null>(null);
  const [active, setActive] = useState<{ tool: Tool; comment?: Comment } | null>(null);
  const [selected, setSelected] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [passage, setPassage] = useState('');
  const [participant, setParticipant] = useState('');
  const [duration, setDuration] = useState('hour');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [noteReady, setNoteReady] = useState(true);
  const comments = useCommentStore((s) => s.comments);
  const [search, setSearch] = useState<{
    total: number;
    results: (SourceComment & { parent: SourceComment | null })[];
  } | null>(null);
  const [sources, setSources] = useState<{ url: string; commentIds: (string | null)[] }[] | null>(
    null,
  );
  const ownPost = currentUser?.id === post.authorId;
  const revision = useRef(0);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    const version = ++revision.current;
    const next = await discussionTools.get(post.id);
    if (version === revision.current) setState(next);
  }, [post.id]);
  useEffect(() => {
    let live = true;
    const version = ++revision.current;
    void discussionTools
      .get(post.id)
      .then((s) => {
        if (live && version === revision.current) setState(s);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [post.id, comments]);
  async function run(data: Record<string, unknown>) {
    if (busy || !gate.allow('use discussion tools')) return;
    ++revision.current;
    setError('');
    setBusy(true);
    try {
      const next = await discussionTools.save(post.id, data);
      ++revision.current;
      setState(next);
    } catch {
      setError('Could not save this change. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }
  function open(tool: Tool, comment?: Comment, excerpt?: string) {
    if (busy) return;
    if (tool === 'labels' && !state) {
      setError('Load the discussion before editing its labels.');
      void refresh().catch(() => {});
      return;
    }
    if (!currentUser && !['search', 'sources'].includes(tool)) {
      navigate(`/signin?next=${encodeURIComponent(discussionHref(post.id))}`);
      return;
    }
    if (!['search', 'sources'].includes(tool) && !gate.allow('use discussion tools')) return;
    setActive({ tool, comment });
    setError('');
    setBody(tool === 'labels' ? (state?.private?.labels.join(', ') ?? '') : '');
    setPassage(excerpt ?? comment?.body.slice(0, 600) ?? '');
    setSearch(null);
    setSources(null);
    setParticipant('');
    setNoteReady(tool !== 'bookmark');
    if (tool === 'bookmark') {
      setBusy(true);
      void discussionTools
        .library()
        .then((l) => {
          setBody(
            l.bookmarks.find((b) => b.postId === post.id && b.commentId === (comment?.id ?? null))
              ?.note ?? '',
          );
          setNoteReady(true);
        })
        .catch(() => setError('Could not load your existing note. Close and try again.'))
        .finally(() => setBusy(false));
    }
    if (tool === 'sources') {
      setBusy(true);
      void api<{ url: string; commentIds: (string | null)[] }[]>(`${toolsUrl(post.id)}sources/`)
        .then(setSources)
        .catch(() => setError('Could not load sources. Close and try again.'))
        .finally(() => setBusy(false));
    }
  }
  async function jump(id: string) {
    if (!mounted.current) return;
    const navigationVersion = navigation.current;
    setBusy(true);
    try {
      if (!useCommentStore.getState().comments.some((c) => c.id === id))
        await commentsService.forPost(post.id);
      const ownerNow = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.handle;
      if (
        !mounted.current ||
        navigationVersion !== navigation.current ||
        ownerNow !== currentUser?.handle
      )
        return;
      setActive(null);
      navigate(discussionHref(post.id, id));
      const target = document.getElementById(`comment-${id}`);
      target?.scrollIntoView({ block: 'center' });
      target?.focus({ preventScroll: true });
    } catch {
      setError('Could not load that comment. Try again.');
    } finally {
      setBusy(false);
    }
  }
  async function submit() {
    if (!active || busy) return;
    setBusy(true);
    ++revision.current;
    setError('');
    const { tool, comment } = active;
    try {
      if (tool === 'search') {
        setSearch(
          await api(
            `${toolsUrl(post.id)}search/?q=${encodeURIComponent(body)}&participant=${encodeURIComponent(participant)}`,
          ),
        );
        return;
      }
      if (tool === 'invite') {
        await api('/discussion-tools/invitations/', {
          method: 'POST',
          body: { postId: post.id, handle: body },
        });
      } else if (tool === 'quote' || tool === 'reply' || tool === 'reconsider') {
        const refs =
          tool === 'quote' && comment
            ? [{ commentId: comment.id, excerpt: passage }]
            : tool === 'reply'
              ? selected.map((c) => ({ commentId: c.id, excerpt: c.body.slice(0, 600) }))
              : [];
        const sessionVersion = commentsService.captureSession();
        const result = await api<{ comment: Comment }>(toolsUrl(post.id), {
          method: 'POST',
          body: {
            action: 'reply',
            body,
            references: refs,
            reconsideredId: tool === 'reconsider' ? comment?.id : undefined,
          },
        });
        // The write succeeded. Preserve its result even if a later refresh fails.
        const ownerNow = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.handle;
        if (ownerNow !== currentUser?.handle) return;
        if (!commentsService.acceptCreatedComment(result.comment, sessionVersion)) return;
        setSelected([]);
        await jump(result.comment.id);
      } else {
        const data = {
          action: tool,
          body,
          commentId: comment?.id,
          note: body,
          labels: body
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
          duration,
          reason: body,
        };
        const next = await discussionTools.save(post.id, data);
        ++revision.current;
        setState(next);
      }
      setActive(null);
      void refresh().catch(() =>
        setError(
          'Saved successfully, but the latest discussion details could not be refreshed. Reload to see them.',
        ),
      );
    } catch {
      setError(
        'Could not complete this action. The source may have changed, or your account may not have permission. Check your entries and try again.',
      );
    } finally {
      setBusy(false);
    }
  }
  function select(comment: Comment) {
    if (!currentUser || !gate.allow('reply')) return;
    setSelected((old) =>
      old.some((c) => c.id === comment.id)
        ? old.filter((c) => c.id !== comment.id)
        : old.length < 3
          ? [...old, comment]
          : old,
    );
  }
  const needsBody =
    active && ['reply', 'quote', 'reconsider', 'update', 'invite'].includes(active.tool);
  return (
    <DiscussionToolsContext.Provider
      value={{ state, ownPost, authenticated: !!currentUser, selected, open, select, run }}
    >
      {children}
      {selected.length > 0 && (
        <Card role="region" aria-label="Selected comments">
          <strong>{selected.length} of 3 comments selected</strong>
          {selected.map((c) => (
            <Small key={c.id}>
              {c.body.slice(0, 100)}{' '}
              <Button size="sm" variant="ghost" onClick={() => select(c)}>
                Remove
              </Button>
            </Small>
          ))}
          <Row>
            <Button onClick={() => open('reply')}>Reply together</Button>
            <Button variant="ghost" onClick={() => setSelected([])}>
              Clear selection
            </Button>
          </Row>
        </Card>
      )}
      {error && !active && <p role="alert">{error}</p>}
      {active && (
        <Dialog
          open
          title={titles[active.tool]}
          onClose={() => {
            if (!busy) setActive(null);
          }}
          dismissable={!busy}
        >
          <Stack>
            {active.tool === 'search' && (
              <>
                <Field>
                  Phrase
                  <input value={body} maxLength={200} onChange={(e) => setBody(e.target.value)} />
                </Field>
                <Field>
                  Participant
                  <input
                    value={participant}
                    maxLength={80}
                    placeholder="Name or @handle"
                    onChange={(e) => setParticipant(e.target.value)}
                  />
                </Field>
              </>
            )}
            {active.tool === 'snooze' && (
              <>
                <Small>
                  You keep following. New replies remain available; reply and mention alerts pause
                  for this post and its comment branches.
                </Small>
                <Field>
                  Pause for
                  <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                    <option value="hour">1 hour</option>
                    <option value="tomorrow">24 hours</option>
                    <option value="forever">Until I resume</option>
                    <option value="resume">Resume now</option>
                  </select>
                </Field>
              </>
            )}
            {active.tool === 'labels' && (
              <>
                <Small>Only you can see these labels. Separate labels with commas.</Small>
                <Field>
                  Labels
                  <input value={body} maxLength={819} onChange={(e) => setBody(e.target.value)} />
                </Field>
              </>
            )}
            {active.tool === 'invite' && (
              <>
                <Small>
                  They can accept or decline in their Invitations inbox. An invitation never grants
                  access to a restricted post or enables notifications.
                </Small>
                <Field>
                  Recipient @handle
                  <input value={body} maxLength={80} onChange={(e) => setBody(e.target.value)} />
                </Field>
                <Link to="/discussions?tab=invitations" onClick={() => setActive(null)}>
                  Manage invitations
                </Link>
              </>
            )}
            {active.tool === 'quote' && (
              <>
                <Small>
                  Select or edit an exact passage from the original. The source stays linked.
                </Small>
                <Textarea
                  label="Quoted passage"
                  value={passage}
                  maxLength={600}
                  onChange={(e) => setPassage(e.target.value)}
                />
              </>
            )}
            {active.tool === 'reconsider' && (
              <>
                <Quote>{active.comment?.body}</Quote>
                <Small>
                  Your earlier comment stays unchanged. This follow-up explains what changed your
                  mind.
                </Small>
              </>
            )}
            {active.tool === 'reply' &&
              selected.map((c) => <Quote key={c.id}>{c.body.slice(0, 600)}</Quote>)}
            {['bookmark', 'helpful', 'update', 'quote', 'reply', 'reconsider'].includes(
              active.tool,
            ) && (
              <Textarea
                label={
                  active.tool === 'bookmark'
                    ? 'Private note (optional)'
                    : active.tool === 'helpful'
                      ? 'Why this helped (optional)'
                      : 'Your words'
                }
                value={body}
                maxLength={active.tool === 'helpful' ? 500 : 5000}
                disabled={busy || (active.tool === 'bookmark' && !noteReady)}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
            {active.tool === 'bookmark' && (
              <Small>
                This note is visible only to your account. Find it in Bookmarks → Private notes.
              </Small>
            )}
            {active.tool === 'helpful' && (
              <Small>
                Shown as an author’s pick, with your reason. Readers’ comment sorting stays
                unchanged.
              </Small>
            )}
            {active.tool === 'sources' && sources && (
              <>
                <Field>
                  Filter sources
                  <input value={body} onChange={(e) => setBody(e.target.value)} />
                </Field>
                {sources
                  .filter((s) => s.url.toLowerCase().includes(body.toLowerCase()))
                  .map((s) => (
                    <Card key={s.url}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.url}
                      </a>
                      <Row>
                        {s.commentIds.map((id, i) =>
                          id ? (
                            <Button key={id} variant="ghost" onClick={() => jump(id)}>
                              Source comment {i + 1}
                            </Button>
                          ) : (
                            <Small key="post">Original post</Small>
                          ),
                        )}
                      </Row>
                    </Card>
                  ))}
                {sources.length === 0 && <Small>No links have been shared here yet.</Small>}
              </>
            )}
            {search && (
              <>
                <Small>
                  <MetaRow>
                    <span>{search.total} matching comments</span>
                    {search.total > 100 && <span>First 100 shown; narrow your search</span>}
                  </MetaRow>
                </Small>
                {search.results.map((c) => (
                  <Card key={c.id}>
                    {c.parent && (
                      <Quote>
                        <Small>Replying to {c.parent.handle}</Small>
                        <p>{c.parent.body}</p>
                      </Quote>
                    )}
                    <strong>
                      {c.author} {c.handle}
                    </strong>
                    <p>{c.body}</p>
                    <Button variant="ghost" onClick={() => jump(c.id)}>
                      View in discussion
                    </Button>
                  </Card>
                ))}
              </>
            )}
            {error && <p role="alert">{error}</p>}
            <Row>
              <Button variant="ghost" disabled={busy} onClick={() => setActive(null)}>
                Close
              </Button>
              {active.tool !== 'sources' && (
                <Button
                  disabled={
                    busy ||
                    !noteReady ||
                    !!(needsBody && !body.trim()) ||
                    (active.tool === 'quote' && !passage.trim())
                  }
                  onClick={() => void submit()}
                >
                  {busy
                    ? 'Saving…'
                    : active.tool === 'search'
                      ? 'Search'
                      : active.tool === 'invite'
                        ? 'Send invitation'
                        : active.tool === 'reconsider' ||
                            active.tool === 'reply' ||
                            active.tool === 'quote'
                          ? 'Post reply'
                          : 'Save'}
                </Button>
              )}
            </Row>
          </Stack>
        </Dialog>
      )}
    </DiscussionToolsContext.Provider>
  );
}
